import type { NavigateFunction } from 'react-router-dom';

const RETURN_KEY = 'saee:returnAfterLogin';

function splitPathAndSuffix(full: string): { pathOnly: string; suffix: string } {
  const q = full.indexOf('?');
  const h = full.indexOf('#');
  const cut = Math.min(q === -1 ? full.length : q, h === -1 ? full.length : h);
  return { pathOnly: full.slice(0, cut), suffix: full.slice(cut) };
}

/** First path segment when URL is `/en/...` or `/ar/...` (ignores query/hash on fallback). */
function brokerLangFromPath(path: string): 'en' | 'ar' | null {
  const { pathOnly } = splitPathAndSuffix(path);
  const parts = pathOnly.split('/').filter(Boolean);
  const first = parts[0];
  return first === 'en' || first === 'ar' ? first : null;
}

/** Keep path + query + hash but replace `/en/` ↔ `/ar/` prefix so post-auth matches the active auth locale. */
function rewriteBrokerPathLang(full: string, newLang: 'en' | 'ar'): string | null {
  const { pathOnly, suffix } = splitPathAndSuffix(full);
  const parts = pathOnly.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  if (parts[0] !== 'en' && parts[0] !== 'ar') return null;
  parts[0] = newLang;
  return `/${parts.join('/')}${suffix}`;
}

/** Do not use auth wizard URLs as post-login targets. */
const AUTH_FLOW_SEGMENTS = new Set([
  'signup',
  'signup-verify',
  'signup-complete',
  'nafath-verification',
  'select-national-address',
  'success',
  'access-denied',
  'forbidden',
]);

/**
 * Allowed post-login paths: /en/... or /ar/... only (open-redirect safe), not auth-only routes.
 */
export function isSafeBrokerAppPath(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const q = path.indexOf('?');
  const h = path.indexOf('#');
  const end = [q === -1 ? path.length : q, h === -1 ? path.length : h].reduce((a, b) => Math.min(a, b));
  const pathOnly = path.slice(0, end);
  const parts = pathOnly.split('/').filter(Boolean);
  if (parts.length < 2) return false;
  const lang = parts[0];
  if (lang !== 'en' && lang !== 'ar') return false;
  const segment = parts[1] ?? '';
  if (AUTH_FLOW_SEGMENTS.has(segment)) return false;
  return true;
}

export function storeReturnAfterLogin(path: string): void {
  if (typeof window === 'undefined') return;
  if (!isSafeBrokerAppPath(path)) return;
  try {
    sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    /* ignore quota / private mode */
  }
}

/** Peek stored return path without removing it (drops invalid values from storage). */
export function readReturnAfterLogin(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(RETURN_KEY);
    if (!v) return null;
    if (isSafeBrokerAppPath(v)) return v;
    sessionStorage.removeItem(RETURN_KEY);
    return null;
  } catch {
    return null;
  }
}

/** Read and remove stored path (one-time use after successful auth). */
export function consumeReturnAfterLogin(): string | null {
  const v = readReturnAfterLogin();
  if (v) clearReturnAfterLogin();
  return v;
}

export function clearReturnAfterLogin(): void {
  try {
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * After OTP / auth success: go to stored deep link if any, but **match the locale of `fallback`**
 * so Arabic signup does not land on an old `/en/...` return URL from sessionStorage.
 */
export function navigateAfterAuth(navigate: NavigateFunction, fallback: string): void {
  const preferredLang = brokerLangFromPath(fallback);
  const stored = readReturnAfterLogin();

  if (stored && preferredLang) {
    const rewritten = rewriteBrokerPathLang(stored, preferredLang);
    if (rewritten && isSafeBrokerAppPath(rewritten)) {
      clearReturnAfterLogin();
      navigate(rewritten, { replace: true });
      return;
    }
    clearReturnAfterLogin();
    navigate(fallback, { replace: true });
    return;
  }

  if (stored) {
    clearReturnAfterLogin();
    navigate(stored, { replace: true });
    return;
  }

  navigate(fallback, { replace: true });
}
